import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Alert, Button, Collapse, Drawer, Skeleton, Space, Tag, Tooltip } from 'antd'
import { CopyOutlined, DatabaseOutlined, EditOutlined } from '@ant-design/icons'
import { useToast } from '@/contexts/ToastContext'
import { useCanManageMcp, useMcpCatalog, useMcpPlaybook } from '../hooks'
import { catalogEntries, catalogIdSplitter, runPrompt } from './text'
import PlaybookInputs from './PlaybookInputs'
import PlaybookTags from './PlaybookTags'

type PlaybookDrawerProps = {
  clientId: string
  playbookId: string | null
  onClose: () => void
  /** Left out when the user can't save playbooks. */
  onEdit?: (id: string) => void
}

export default function PlaybookDrawer({ clientId, playbookId, onClose, onEdit }: PlaybookDrawerProps) {
  const navigate = useNavigate()
  const { addToast } = useToast()
  const { canManage: canOpenFirmProfile } = useCanManageMcp(clientId)
  const playbook = useMcpPlaybook(clientId, playbookId)
  const catalog = useMcpCatalog(clientId)
  const splitCatalogIds = useMemo(() => catalogIdSplitter(catalogEntries(catalog.data)), [catalog.data])
  const data = playbook.data
  const prompt = data ? runPrompt(data) : ''

  const copyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(prompt)
      addToast('Copied. Paste it into your AI assistant.', 'success')
    } catch {
      addToast('Couldn’t copy. Select the text and copy it yourself.', 'warning')
    }
  }

  return (
    <Drawer
      open={Boolean(playbookId)}
      onClose={onClose}
      width={600}
      destroyOnHidden
      title={data?.name ?? 'Playbook'}
      extra={
        data?.canEdit && onEdit ? (
          <Button icon={<EditOutlined />} onClick={() => onEdit(data.id)}>
            Edit
          </Button>
        ) : null
      }
    >
      {playbook.isLoading && <Skeleton active paragraph={{ rows: 8 }} />}
      {playbook.isError && (
        <Alert
          type="error"
          showIcon
          message={playbook.error instanceof Error ? playbook.error.message : 'Couldn’t load this playbook.'}
        />
      )}
      {data && (
        <Space orientation="vertical" size={20} className="w-full">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              {data.when && <span className="text-sm text-gray-500">{data.when}</span>}
              <PlaybookTags playbook={data} />
            </div>
            {data.description && <p className="mt-2 mb-0 text-sm text-gray-700">{data.description}</p>}
          </div>

          {data.adminOnly && !data.canEdit && (
            <Alert
              type="info"
              showIcon
              message="This is the onboarding. Its answers are on the Firm profile page."
              action={
                canOpenFirmProfile ? (
                  <Button size="small" onClick={() => navigate('/reports/firm-profile')}>
                    Open the firm profile
                  </Button>
                ) : undefined
              }
            />
          )}

          {data.output.length > 0 && (
            <section>
              <h3 className="font-display text-sm font-semibold text-gray-900 mb-2">You’ll get</h3>
              <ul className="m-0 flex list-none flex-wrap gap-1.5 p-0">
                {data.output.map((heading) => (
                  <li key={heading}>
                    <Tag bordered={false} className="!m-0 !rounded-full !px-2.5 !py-0.5 !text-sm">
                      {heading}
                    </Tag>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {data.inputs.length > 0 && (
            <section>
              <h3 className="font-display text-sm font-semibold text-gray-900 mb-2">Inputs</h3>
              <PlaybookInputs inputs={data.inputs} />
            </section>
          )}

          <Collapse
            items={[
              {
                key: 'steps',
                label: <span className="font-display text-sm font-semibold text-gray-900">How it works</span>,
                extra: (
                  <span className="text-xs text-gray-500">
                    {data.steps.length} {data.steps.length === 1 ? 'step' : 'steps'}
                  </span>
                ),
                children: (
                  <ol className="m-0 space-y-2 pl-5 text-sm text-gray-700">
                    {data.steps.map((step, index) => (
                      <li key={index} className="leading-6">
                        {step.query && (
                          <Tooltip title="Gets data">
                            <DatabaseOutlined aria-label="Gets data" className="mr-1.5 text-gray-500" />
                          </Tooltip>
                        )}
                        {splitCatalogIds(step.text).map((segment, part) =>
                          segment.entry ? (
                            <Tooltip key={part} title={segment.entry.id}>
                              <span className="rounded bg-brand-50 px-1 font-medium text-brand-700">
                                {segment.entry.label}
                              </span>
                            </Tooltip>
                          ) : (
                            <span key={part}>{segment.text}</span>
                          )
                        )}
                      </li>
                    ))}
                  </ol>
                ),
              },
            ]}
          />

          <section className="rounded-lg border border-brand-100 bg-brand-25 p-3">
            <h3 className="font-display text-sm font-semibold text-gray-900 mb-2">To run it</h3>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Ask your AI assistant:</span>
              <code className="grow rounded bg-white px-2 py-1 text-sm text-gray-900">{prompt}</code>
              <Tooltip title="Copy">
                <Button icon={<CopyOutlined />} aria-label="Copy what to ask your AI assistant" onClick={() => void copyPrompt()} />
              </Tooltip>
            </div>
          </section>
        </Space>
      )}
    </Drawer>
  )
}
